import React, { useCallback } from 'react';
import { dismissFlag as createDismissFlag, useFlagService, InfoFlag } from '@atlassian/jira-flags';

import {
	RequestAccessPromptFlag,
	type CrossJoinIntegraton,
	type CrossJoinTargetProduct,
} from '@atlassian/cross-join-nudges';

import {
	fireUIAnalytics,
	useAnalyticsEvents,
	type AnalyticsAttributes,
} from '@atlassian/jira-product-analytics-bridge';
import type { Collaborators } from '../../services/get-collaborators/types.tsx';
import { generateId } from '../../utils/index.tsx';

import { getMsrFeatureVisitedCount } from '../../services/msr/caching.tsx';

export const useMsrRequestAccessPromptFlag = () => {
	const { showFlag, dismissFlag } = useFlagService();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	return useCallback(
		async ({
			users,
			onSubmit,
			targetProduct,
			integration,
			eventAttributes,
		}: {
			users: Collaborators;
			onSubmit: () => Promise<void>;
			targetProduct: CrossJoinTargetProduct;
			integration: CrossJoinIntegraton;
			eventAttributes: AnalyticsAttributes;
		}) => {
			const id = `request-access-${generateId()}`;

			const dismissTheFlag = () => dismissFlag(createDismissFlag(id));

			const featureVisitedCount = getMsrFeatureVisitedCount();

			const submit = async () => {
				fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'msrNudge', {
					...eventAttributes,
					featureVisitedCount,
				});
				dismissTheFlag();
				await onSubmit();
			};

			const dismiss = () => {
				fireUIAnalytics(createAnalyticsEvent({}), 'flag dismissed', 'msrNudge', {
					...eventAttributes,
					featureVisitedCount,
				});
				dismissTheFlag();
			};

			const dismissAndSnooze = () => {
				fireUIAnalytics(createAnalyticsEvent({}), 'flag snoozed', 'msrNudge', {
					...eventAttributes,
					featureVisitedCount,
				});
				dismissTheFlag();
			};

			fireUIAnalytics(createAnalyticsEvent({}), 'nudge viewed', 'msrNudge', {
				...eventAttributes,
				featureVisitedCount,
			});

			showFlag({
				id,
				render: (props) => (
					<RequestAccessPromptFlag
						{...props}
						id={id}
						collaborators={users}
						onDismiss={dismiss}
						onSnoozeAndDismiss={dismissAndSnooze}
						onOptoutAndDismiss={dismissAndSnooze}
						onSubmit={submit}
						integration={integration}
						targetProduct={targetProduct}
						viewCount={featureVisitedCount}
						flagComponent={InfoFlag}
					/>
				),
			});
		},
		[createAnalyticsEvent, dismissFlag, showFlag],
	);
};

type CommonAnalyticsArgs = {
	users: Collaborators;
	userId: string;
	recommendationSessionId: string;
	featureVisitedCount?: number;
	integration?: CrossJoinIntegraton;
	nudgeId: string;
	recommendations: string[];
};

export const getAnalyticsAttributes = ({
	users,
	userId,
	recommendationSessionId,
	integration,
	nudgeId,
	recommendations,
}: CommonAnalyticsArgs): AnalyticsAttributes => {
	return {
		collaborators: users.length,
		userId,
		recommendationSessionId,
		integration,
		nudgeId,
		recommendedNudges: recommendations.join(','),
	};
};
